import {Chip} from '@dropbox/dig-components/chip';
import {FormRow} from '@dropbox/dig-components/form_row';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Toggletip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Cluster, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PersonMultipleLine, TeamLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {EmployeeLdap, Team, TeamWithCounts, WorkstreamEmployeeEdit} from 'client';
import {GlobalSearchTypeahead} from 'components/DSYS/GlobalSearchTypeahead';
import {isTeam} from 'hooks/useEmployee';
import {useProfiles} from 'hooks/useProfile';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {Suspense, useState} from 'react';

import {WorkstreamPrivacyList} from './WorkstreamPrivacyList';

const PeopleCount = ({
  owners,
  selectedEmployees,
}: {
  owners: WorkstreamEmployeeEdit[];
  selectedEmployees: EmployeeLdap[];
}) => {
  const count = useEmployeeCount(
    owners.map(({user_id}) => user_id),
    selectedEmployees.map(({user_id}) => user_id)
  );

  return <Text isBold>{t('people_count', {count})}</Text>;
};

const dedupeTeam = (employeeTeams: Team[], teamPrivacies?: Team[]) =>
  employeeTeams.filter((e) => !teamPrivacies?.some(({team_id}) => team_id === e.team_id));

const dedupeEmployees = (employees: EmployeeLdap[], employeePrivacies?: EmployeeLdap[]) =>
  employees.filter((e) => !employeePrivacies?.some(({user_id}) => user_id === e.user_id));

const CHIP_STYLE = {
  height: '24px',
  maxWidth: '100%',
  minWidth: 0,
} as const;

export const WorkstreamPrivacyDrawer = ({
  readOnly,
  selectedEmployees = [],
  selectedTeams = [],
  owners = [],
  setSelectedEmployees,
  setSelectedTeams,
}: {
  selectedEmployees?: EmployeeLdap[];
  selectedTeams?: TeamWithCounts[];
  owners?: WorkstreamEmployeeEdit[];
  readOnly?: boolean;
  setSelectedEmployees?: (employees: EmployeeLdap[]) => void;
  setSelectedTeams?: (teams: TeamWithCounts[]) => void;
}) => {
  const {employeeTeams, directReports} = useAtomValue(loggedInEmployeeAtom);

  const [employeeTeamChips, setEmployeeTeamChips] = useState<Team[]>(
    dedupeTeam(employeeTeams ?? [], selectedTeams) || []
  );
  const [directReportsChip, setDirectReportsChip] = useState<EmployeeLdap[]>(
    dedupeEmployees(directReports) || []
  );

  const onTeamChipClick = (team: Team) => {
    if (setSelectedTeams) {
      setSelectedTeams([team as TeamWithCounts, ...selectedTeams]);
    }
    setEmployeeTeamChips((prev) => prev.filter((c) => c.team_id !== team.team_id));
  };

  const onDirectReportsChipClick = () => {
    if (setSelectedEmployees) {
      setSelectedEmployees(dedupeEmployees([...directReportsChip, ...selectedEmployees]));
    }
    setDirectReportsChip([]);
  };

  const onRemoveEmployeeOrTeam = (item: EmployeeLdap | Team) => {
    if (isTeam(item)) {
      setSelectedTeams?.(selectedTeams.filter(({team_id}) => team_id !== item.team_id));
    } else {
      setSelectedEmployees?.(selectedEmployees.filter(({user_id}) => user_id !== item.user_id));
    }
  };

  return (
    <>
      {!readOnly && (
        <>
          <Stack gap="12">
            <Stack display="flex" gap="8">
              <Cluster>
                <Cluster.Item>
                  <Text isBold size="large">
                    {t('custom')}
                  </Text>
                </Cluster.Item>
                <Cluster.Item>
                  <Toggletip title={t('choose_who_may_view_and_align_to_this_workstream')} />
                </Cluster.Item>
              </Cluster>
              <FormRow>
                <GlobalSearchTypeahead
                  filter="people,teams"
                  selections={[
                    ...selectedEmployees.map((e) => e.user_id),
                    ...selectedTeams.map((team) => team.team_id),
                  ]}
                  onSelect={(name, type, uid, id) => {
                    if (type === 'employee') {
                      setSelectedEmployees?.([
                        ...selectedEmployees,
                        {
                          user_id: id,
                          ldap: uid,
                          name: name,
                        },
                      ]);
                    } else {
                      setSelectedTeams?.([
                        ...selectedTeams,
                        {
                          team_id: id,
                          slug: uid,
                          name,
                        } as Team,
                      ]);
                    }
                  }}
                />
                <Box
                  data-testid="privacy-audience-suggestion-chips"
                  display="flex"
                  style={{
                    columnGap: '6px',
                    rowGap: '6px',
                    marginTop: '-12px',
                    flexWrap: 'wrap',
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'hidden',
                  }}
                >
                  {directReportsChip && directReportsChip.length > 0 && (
                    <Box display="inline-flex" style={{maxWidth: '100%'}}>
                      <Chip
                        variant="standard"
                        style={CHIP_STYLE}
                        onClick={onDirectReportsChipClick}
                      >
                        <Chip.IconAccessory>
                          <UIIcon src={PersonMultipleLine} />
                        </Chip.IconAccessory>
                        <Chip.Content>{t('reports')}</Chip.Content>
                      </Chip>
                    </Box>
                  )}
                  {employeeTeamChips.map((team) => (
                    <Box key={team.team_id} display="inline-flex" style={{maxWidth: '100%'}}>
                      <Chip
                        variant="standard"
                        style={CHIP_STYLE}
                        onClick={() => onTeamChipClick(team)}
                      >
                        <Chip.IconAccessory>
                          <UIIcon src={TeamLine} />
                        </Chip.IconAccessory>
                        <Chip.Content>
                          <Truncate maxWidth={220}>{team.name}</Truncate>
                        </Chip.Content>
                      </Chip>
                    </Box>
                  ))}
                </Box>
              </FormRow>
            </Stack>
          </Stack>

          <Box
            marginBottom="20"
            backgroundColor="Border Subtle"
            style={{
              width: 'calc(100% + 48px)',
              height: 1,
              marginLeft: -24,
            }}
          />
        </>
      )}

      <Stack gap="8">
        <Suspense fallback={<Skeleton.Box height={17} width={105} />}>
          <PeopleCount owners={owners} selectedEmployees={selectedEmployees} />
        </Suspense>
        {selectedTeams.length > 0 && (
          <>
            <Text> {t('and')} </Text>
            <Text isBold>{t('team', {count: selectedTeams.length})}</Text>
          </>
        )}
        <Text> {t('may_view_and_align_to_this_workstream')}</Text>
        <Suspense fallback={<Skeleton.Box height={17} width={105} />}>
          <PrivacyList
            readOnly={readOnly}
            owners={owners}
            privateEmployees={selectedEmployees}
            privateTeams={selectedTeams}
            onRemoveEmployeeOrTeam={onRemoveEmployeeOrTeam}
          />
        </Suspense>
      </Stack>
    </>
  );
};

const PrivacyList = ({
  readOnly,
  owners,
  privateEmployees,
  privateTeams,
  onRemoveEmployeeOrTeam,
}: {
  readOnly?: boolean;
  owners: WorkstreamEmployeeEdit[];
  privateEmployees: EmployeeLdap[];
  privateTeams: TeamWithCounts[];
  onRemoveEmployeeOrTeam: (item: EmployeeLdap | Team) => void;
}) => {
  const {employee, delegatedBy} = useAtomValue(loggedInEmployeeAtom);
  // const ownerEmployees = useEmployees({ldaps: owners.map(({user_id}) => user_id)});
  const ownerEmployees = useProfiles({ldaps: owners.map(({user_id}) => user_id), enabled: true});

  // you can delete if the employee is any of the owners or a delegate of any of them
  const canDelete =
    !readOnly &&
    owners.some(
      ({user_id}) => user_id === employee.user_id || delegatedBy?.some((d) => d.user_id === user_id)
    );

  if (!owners.length) {
    return null;
  }

  const dedupedOwners = ownerEmployees
    .map((owner) => owner.employee)
    .map((owner) => ({
      ...owner,
      dri: owners.find((o) => o.user_id === owner.user_id)?.dri ?? 'author',
    })) as (EmployeeLdap & {dri: string})[];

  const dedupedReportingLine = Array.from(
    new Set(
      ownerEmployees
        .flatMap((owner) => owner.reportingLine ?? [])
        .filter(Boolean)
        .map((reportingLineEmployee) => reportingLineEmployee.user_id)
    )
  )
    .map((userId) => {
      const foundEmployee = ownerEmployees
        .flatMap((owner) => owner.reportingLine ?? [])
        .find((e) => e.user_id === userId);
      return foundEmployee
        ? {
            user_id: foundEmployee.user_id,
            ldap: foundEmployee.ldap,
            name: foundEmployee.name,
          }
        : null;
    })
    .filter(
      (reportingLineEmployee): reportingLineEmployee is EmployeeLdap =>
        reportingLineEmployee !== null &&
        !dedupedOwners.some((owner) => owner.user_id === reportingLineEmployee.user_id)
    );

  const delegates = Array.from(
    new Set(
      ownerEmployees
        .flatMap((owner) => owner.delegates ?? [])
        .filter(
          (delegate) =>
            !dedupedOwners.some((owner) => owner.user_id === delegate.user_id) &&
            !privateEmployees.some((p) => p.user_id === delegate.user_id) &&
            !dedupedReportingLine.some((r) => r.user_id === delegate.user_id)
        )
        .map((delegate) => delegate.user_id)
    )
  )
    .map((userId) => {
      const foundDelegate = ownerEmployees
        .flatMap((owner) => owner.delegates ?? [])
        .find((d) => d.user_id === userId);
      return foundDelegate
        ? {
            user_id: foundDelegate.user_id,
            ldap: foundDelegate.ldap,
            name: foundDelegate.name,
          }
        : null;
    })
    .filter((delegate): delegate is EmployeeLdap => delegate !== null);

  const dedupedPrivateEmployees = privateEmployees.filter(
    (privateEmployee) =>
      !dedupedOwners.some((owner) => owner.user_id === privateEmployee.user_id) &&
      !delegates.some((delegate) => delegate.user_id === privateEmployee.user_id) &&
      !dedupedReportingLine.some((r) => r.user_id === privateEmployee.user_id)
  );

  return (
    <WorkstreamPrivacyList
      selectedEmployeesOrTeams={[...dedupedPrivateEmployees, ...privateTeams]}
      onRemoveEmployeeOrTeam={onRemoveEmployeeOrTeam}
      delegates={delegates}
      reportingLine={dedupedReportingLine}
      owners={dedupedOwners}
      canDelete={canDelete}
    />
  );
};

export const useEmployeeCount = (
  ownerIds: string[],
  privateEmployees: string[],
  enabled = true,
  suspense = true
) => {
  const ownerEmployees = useProfiles({ldaps: ownerIds, enabled, suspense});

  if (!ownerIds?.length) return privateEmployees?.length;

  const uniqueEmployees = new Set(
    [
      ...ownerIds,
      ...(ownerEmployees?.map(({employee}) => employee.user_id) ?? []),
      ...(ownerEmployees?.flatMap(({delegates}) => delegates?.map((d) => d.user_id) ?? []) ?? []),
      ...(ownerEmployees?.flatMap(
        ({reportingLine}) => reportingLine?.map((r) => r.user_id) ?? []
      ) ?? []),
      ...privateEmployees,
    ].filter(Boolean)
  );

  return uniqueEmployees.size;
};

export const CustomWorkstreamPrivacySubtext = ({
  employeeCount,
  teamCount,
}: {
  employeeCount: number;
  teamCount: number;
}) => (
  <>
    {t('people_count', {count: employeeCount})}
    {teamCount > 0 && (
      <>
        {' '}
        {t('and')} {t('team', {count: teamCount})}
      </>
    )}{' '}
    {t('may_view_and_align_to_this_workstream')}
  </>
);
